/*
 * Section registry. A section's root carries data-section-type="<type>" and data-section-id="{{ section.id }}";
 * its code is src/scripts/sections/types/<type>.js (`export default {onLoad() {...}}`), imported on demand.
 * start() (called once from layout/theme.js) creates one instance per root, picking the moment from the root's
 * data-section-loading:
 *   immediate (or missing)  on DOM ready
 *   in-view                 when the root comes within 200px of the viewport
 *   interaction             after the visitor's first interaction, during idle time, one type at a time; a click
 *                           inside the root before then mounts it right away and is replayed once it's ready
 *   legacy                  never: the section loads its own script (register() still works for that)
 * In the theme editor everything loads right away, and re-rendered sections are created again.
 * Each root gets data-section-initialized="true" once its instance exists.
 *
 * Instances inherit the definition, so lifecycle methods (and the definition's own methods) run with:
 *   this.id / this.type / this.container
 *   this.on(target, event, handler, options)  addEventListener, removed automatically on unload
 *   this.cleanup(fn)                           any other teardown (observers, Swiper.destroy, timers), run on unload
 *   this.unloaded                              true once unloaded: check it after an await in an async onLoad
 *
 * Lifecycle: onLoad, onUnload, onSelect, onDeselect, onBlockSelect(event), onBlockDeselect(event).
 */
import loaded from '@/scripts/utils/loaded.js';
import importSection from '@/scripts/core/section-importer.js';
import onFirstInteraction from '@/scripts/utils/first-interaction.js';
import {whenIdle, yieldToMain} from '@/scripts/utils/idle.js';

const SELECTOR = '[data-section-type]';
const IN_VIEW_MARGIN = '200px 0px';

const definitions = new Map();
const instances = new Map();

const base = {
    on(target, event, handler, options) {
        target.addEventListener(event, handler, options);
        this.cleanup(() => target.removeEventListener(event, handler, options));
        return handler;
    },

    // Registered after unload (an async onLoad that resumed late): there is nothing left to wait for.
    cleanup(fn) {
        if (this.unloaded) {
            fn();
            return;
        }
        this.cleanups.push(fn);
    },
};

function call(instance, method, ...args) {
    if (typeof instance[method] !== 'function') {
        return;
    }

    const fail = (error) => console.error(`[sections] ${instance.type}#${instance.id} ${method} failed`, error);
    try {
        Promise.resolve(instance[method](...args)).catch(fail);
    } catch (error) {
        fail(error);
    }
}

function containersIn(root, type) {
    const containers = Array.from(root.querySelectorAll(SELECTOR));
    if (root instanceof Element && root.matches(SELECTOR)) {
        containers.unshift(root);
    }

    return type === '*' ? containers : containers.filter((container) => container.dataset.sectionType === type);
}

function create(container) {
    const definition = definitions.get(container.dataset.sectionType);
    if (!definition || instances.has(container)) {
        return;
    }

    const instance = Object.create(definition);
    instance.id = container.dataset.sectionId;
    instance.type = container.dataset.sectionType;
    instance.container = container;
    instance.cleanups = [];
    instance.unloaded = false;

    instances.set(container, instance);
    call(instance, 'onLoad');
    container.setAttribute('data-section-initialized', 'true');
}

function define(type, definition) {
    if (!definitions.has(type)) {
        definitions.set(type, Object.assign(Object.create(base), definition));
    }
}

// Import a type's code once, however many roots ask for it. Resolves false when the type has no script.
const imports = new Map();

function ensureDefinition(type) {
    if (definitions.has(type)) {
        return Promise.resolve(true);
    }
    if (!imports.has(type)) {
        imports.set(type, importSection(type)
            .then((definition) => {
                if (definition) {
                    define(type, definition);
                }
                return Boolean(definition);
            })
            .catch((error) => {
                imports.delete(type);
                console.error(`[sections] ${type} failed to load`, error);
                return false;
            }));
    }
    return imports.get(type);
}

async function mount(container) {
    const type = container.dataset.sectionType;
    if (!type || instances.has(container)) {
        return;
    }
    if (await ensureDefinition(type) && container.isConnected) {
        create(container);
    }
}

function destroy(instance) {
    instance.unloaded = true;
    call(instance, 'onUnload');
    instance.cleanups.splice(0).reverse().forEach((fn) => {
        try {
            fn();
        } catch (error) {
            console.error(`[sections] ${instance.type}#${instance.id} cleanup failed`, error);
        }
    });
    instances.delete(instance.container);
}

function instancesIn(root) {
    return Array.from(instances.values()).filter((instance) => root === instance.container || root.contains(instance.container));
}

function instancesOf(sectionId) {
    return Array.from(instances.values()).filter((instance) => instance.id === sectionId);
}

export function load(type = '*', root = document) {
    containersIn(root, type).forEach(create);
}

export function unload(type = '*', root = document) {
    instancesIn(root === document ? document.documentElement : root)
        .filter((instance) => type === '*' || instance.type === type)
        .forEach(destroy);
}

export function getInstances(sectionId) {
    return sectionId ? instancesOf(sectionId) : Array.from(instances.values());
}

// The theme editor re-renders a section by replacing its wrapper (event.target) and announcing it on document.
let editorListening = false;

function listenToEditor() {
    if (editorListening) {
        return;
    }
    editorListening = true;

    document.addEventListener('shopify:section:load', (event) => containersIn(event.target, '*').forEach(mount));
    document.addEventListener('shopify:section:unload', (event) => unload('*', event.target));
    document.addEventListener('shopify:section:select', (event) => instancesIn(event.target).forEach((instance) => call(instance, 'onSelect', event)));
    document.addEventListener('shopify:section:deselect', (event) => instancesIn(event.target).forEach((instance) => call(instance, 'onDeselect', event)));
    document.addEventListener('shopify:block:select', (event) => instancesOf(event.detail?.sectionId).forEach((instance) => call(instance, 'onBlockSelect', event)));
    document.addEventListener('shopify:block:deselect', (event) => instancesOf(event.detail?.sectionId).forEach((instance) => call(instance, 'onBlockDeselect', event)));
}

// A `display: contents` root (.section-root) has no box, so an IntersectionObserver never fires for it:
// watch its children instead.
function viewTargets(container) {
    return getComputedStyle(container).display === 'contents' ? Array.from(container.children) : [container];
}

function mountInView(containers) {
    const owners = new Map();
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const container = owners.get(entry.target);
            if (!entry.isIntersecting || !container) {
                return;
            }
            viewTargets(container).forEach((target) => {
                observer.unobserve(target);
                owners.delete(target);
            });
            mount(container);
        });
    }, {rootMargin: IN_VIEW_MARGIN});

    containers.forEach((container) => {
        const targets = viewTargets(container);
        if (!targets.length) {
            mount(container);
            return;
        }
        targets.forEach((target) => {
            owners.set(target, container);
            observer.observe(target);
        });
    });
}

// A click inside an `interaction` root that isn't mounted yet (often the visitor's first tap) would reach no
// handler: hold it, mount the root now, then click the same element again. If the code can't load, the replayed
// click does what the markup alone does (e.g. follows the link).
function replayEarlyClicks(container) {
    const onClick = (event) => {
        container.removeEventListener('click', onClick, true);
        if (instances.has(container)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const {target} = event;
        mount(container).finally(() => target.isConnected && target.click());
    };
    container.addEventListener('click', onClick, true);
}

function mountOnInteraction(containers) {
    containers.forEach(replayEarlyClicks);
    onFirstInteraction(() => whenIdle(async () => {
        const byType = new Map();
        containers.forEach((container) => {
            const list = byType.get(container.dataset.sectionType) || [];
            list.push(container);
            byType.set(container.dataset.sectionType, list);
        });
        // One type per task, so a burst of section setup doesn't delay the visitor's next input.
        for (const list of byType.values()) {
            await Promise.all(list.map(mount));
            await yieldToMain();
        }
    }));
}

let started = false;

export function start() {
    if (started) {
        return;
    }
    started = true;
    listenToEditor();

    loaded(() => {
        const designMode = Boolean(window.Shopify?.designMode);
        const groups = {immediate: [], 'in-view': [], interaction: []};

        document.querySelectorAll(SELECTOR).forEach((container) => {
            if (instances.has(container)) {
                return;
            }
            const loading = designMode ? 'immediate' : (container.dataset.sectionLoading || 'immediate').toLowerCase();
            if (loading === 'legacy') {
                return;
            }
            (groups[loading] || groups.immediate).push(container);
        });

        groups.immediate.forEach(mount);
        if (groups['in-view'].length) {
            mountInView(groups['in-view']);
        }
        if (groups.interaction.length) {
            mountOnInteraction(groups.interaction);
        }
    });
}

export function register(type, definition) {
    if (definitions.has(type)) {
        console.warn(`[sections] "${type}" is already registered`);
        return;
    }

    define(type, definition);
    listenToEditor();
    loaded(() => load(type));
}
